import React from 'react';
import { fabric } from 'fabric';
import EmbeddedTextBehaviorsV2, {
  type TextProps,
  IMAGE_SCALE,
} from './EmbeddedTextBehaviorsV2';
import smartZoneIconSvg from '../assets/icons/smartZoneIcon.svg';
import layoutEnableIconSvg from '../assets/icons/layoutEnable.svg';
import { svgToURL } from '../utils/svg';
import { pointInRect } from '../utils/geometry';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import LockBehaviors from './LockBehaviors';
import CommentableBehaviors from './CommentableBehaviors';
import HighlightBehaviors from './HighlightBehaviors';
import LayoutBehaviors from './LayoutBehaviors';
import { type HasEmbeddedText } from '../types';
import {
  ToolFillColor,
  ToolFillColorWithoutOpacity,
} from '../tools/ToolFillColor';
import { ToolFrameVisibility } from '../tools/ToolFrameVisibility';
import ToolFrameSize from '../tools/ToolFrameSize';
import { type ToolList } from '../fabric/ObjectBehaviors';
import FrameBehaviors from './FrameBehaviors';
import TitleBehaviors from './TitleBehaviors';
import ShapePersistentControlBehaviors from './ShapePersistentControlBehaviors';
import FrameMemberBehaviors from './FrameMemberBehaviors';
import AnalyticsBehaviors from './AnalyticsBehaviors';
import ReadOnlyBehaviors from './ReadOnlyBehaviors';
import SmartGuideBehaviors from './SmartGuideBehaviors';
import CollaborationBehaviors from './CollaborationBehaviors';
import VotingBehaviors from './VotingBehaviors';
import SmartZoneBehaviors from './SmartZoneBehaviors';
import ShapeBase from '../shapes/ShapeBase';
import type TextField from './TextField';
import ShapeLinearResizeControl from '../controls/ShapeLinearResizeControl';
import ToolLayoutEnable from '../tools/ToolLayoutEnable';
import ToolLayoutAlignment from '../tools/ToolLayoutAlignment';
import ToolLayoutOrientation from '../tools/ToolLayoutOrientation';
import ToolLayoutSizeSpacing from '../tools/ToolLayoutSizeSpacing';
import {
  ToolEditSmartZone,
  ToolEnableSmartZone,
} from '../tools/smartZoneTools';
import { hasFrameMemberBehaviors, isSimpleSelection } from './guards';
import { SPACER_2 } from '../utils/style';
import LinkToUrlBehaviors, {
  type LinkToUrlBehaviorsInterface,
} from './LinkToUrlBehaviors';
import { type Point } from 'fabric/fabric-impl';

const STYLIZED_COMPONENTS = ['stack', 'swimlane'];

const TITLE_ICON_HEIGHT_ADJUSTMENT = 1.6;

// A frame that surrounds, but doesn't group, a set of objects. The objects
// that are controlled by a frame have the ID of the frame in their `frame`
// property. By storing the membership on the object we avoid bugs with
// the same object being in multiple frames.
interface Frame
  extends LayoutBehaviors,
    FrameBehaviors,
    TitleBehaviors,
    EmbeddedTextBehaviorsV2,
    HasEmbeddedText,
    HighlightBehaviors,
    LockBehaviors,
    CommentableBehaviors,
    FrameMemberBehaviors,
    ReadOnlyBehaviors,
    SmartGuideBehaviors,
    CollaborationBehaviors,
    LinkToUrlBehaviorsInterface,
    VotingBehaviors,
    ShapePersistentControlBehaviors,
    SmartZoneBehaviors {}

@LayoutBehaviors
@FrameBehaviors
@TitleBehaviors
@EmbeddedTextBehaviorsV2
@HighlightBehaviors
@LockBehaviors
@CommentableBehaviors
@FrameMemberBehaviors
@AnalyticsBehaviors
@ReadOnlyBehaviors
@SmartGuideBehaviors
@CollaborationBehaviors
@LinkToUrlBehaviors
@VotingBehaviors
@ShapePersistentControlBehaviors
@SmartZoneBehaviors
class Frame extends fabric.Object {
  static smartZoneIcon: HTMLImageElement;
  static smartZoneIconLoaded = false;
  static autoLayoutIcon: HTMLImageElement;
  static autoLayoutIconLoaded = false;

  type = 'frame';
  // Whether to skip the frame in the list of frames to scroll through
  notInNavigation = false;
  noScaleCache = false;
  objectCaching = false;
  selectionFullyContained = true;
  componentName = '';

  // When a child of this frame is selected, then we don't allow the frame to be
  // selected on the next click
  childSelected = false;
  // Since frames are deselectable by clicking on them, we need to track if the
  // frame moved to avoid accidentally deselecting at the end of the drag
  didChange = false;

  textProps: TextProps = {
    allowedStyles: 'simple',
    location: 'above',
    textAlign: 'left',
    verticalAlign: 'middle',
    fontSize: 'fixed',
    overflow: 'visible',
    wrapText: 'growText',
    margins: 'noScale',
    enterKeyEndsEditing: true,
  };

  sortKey: string | undefined;

  serializedKeys: Array<string | RegExp> = [
    'z',
    'textContent',
    'left',
    'top',
    'width',
    'height',
    'scaleX',
    'scaleY',
    'fill',
    'opacity',
    'container',
    'notInNavigation',
    'sortKey',
    'componentName',
    'smartZoneConfig',
  ];

  controls: Record<string, fabric.Control> = {
    tl: ShapeBase.resizeControl({ x: -0.5, y: -0.5 }),
    tr: ShapeBase.resizeControl({ x: 0.5, y: -0.5 }),
    bl: ShapeBase.resizeControl({ x: -0.5, y: 0.5 }),
    br: ShapeBase.resizeControl({ x: 0.5, y: 0.5 }),

    mt: new ShapeLinearResizeControl({ edge: 'top' }),
    mb: new ShapeLinearResizeControl({ edge: 'bottom' }),
    ml: new ShapeLinearResizeControl({ edge: 'left' }),
    mr: new ShapeLinearResizeControl({ edge: 'right' }),
  };

  static initializeSmartZoneIcon() {
    if (Frame.smartZoneIconLoaded) return;

    // Create a new image and load the SVG file
    Frame.smartZoneIcon = new Image();

    // Convert the imported SVG to a data URL
    Frame.smartZoneIcon.src = svgToURL(smartZoneIconSvg, '#999');

    Frame.smartZoneIconLoaded = true;
  }

  static initializeAutoLayoutIcon() {
    if (Frame.autoLayoutIconLoaded) return;

    // Create a new image and load the SVG file
    Frame.autoLayoutIcon = new Image();

    // Convert the imported SVG to a data URL
    Frame.autoLayoutIcon.src = svgToURL(layoutEnableIconSvg, '#999');

    Frame.autoLayoutIconLoaded = true;
  }

  constructor(options) {
    super({});

    Object.assign(this, options);
    super.setOptions(options);

    if (!this.width) {
      this.set('width', Frame.defaultWidth());
    }
    if (!this.height) {
      this.set('height', Frame.defaultHeight());
    }

    // Initialize the smart zone icon
    Frame.initializeSmartZoneIcon();
    // Initialize the auto layout icon
    Frame.initializeAutoLayoutIcon();

    this.on('scaling', () => {
      this.hideText();
    });

    // We need to keep our text independent of the canvas zoom level. Hide
    // text when zoom level is excessive.
    this.on('zoom', () => {
      this.hideText();
    });

    this.on('keydown', eventData => {
      void this._handleKeyDown(eventData.e);
    });
    this.addText(options.textContent);
    this.initializeLockBehaviors(options.locked);
    this.needsZoomEvents();
    this.initializeSmartGuides();
    this.initializeFrameMember();
    this.enableFrameBehaviors();
    this.initializeLayout();

    if (this.componentName) {
      this.initializeComponent();
    }

    this.on('moving', () => (this.didChange = true));
    this.on('scaling', () => (this.didChange = true));
    this.initFrameSelectionListeners();
  }

  get votingResultIcon(): JSX.Element {
    return <i className='fa-regular fa-frame' />;
  }

  get votingResultSummary(): string {
    return this.plainText();
  }

  tools(): ToolList | null {
    const layoutTools: ToolList = [];
    if (this.isStylizedComponent()) {
      layoutTools.push([ToolLayoutOrientation]);
    } else {
      layoutTools.push([ToolLayoutEnable]);
    }
    const fillColorTool = this.isStylizedComponent()
      ? ToolFillColorWithoutOpacity
      : ToolFillColor;
    const tools: ToolList = [layoutTools, [fillColorTool, ToolFrameVisibility]];

    if (this.layoutEnabled && !this.isStylizedComponent()) {
      layoutTools.push(
        ToolLayoutAlignment,
        ToolLayoutOrientation,
        ToolLayoutSizeSpacing
      );
    }

    if (this.children.length > 0) {
      tools.push(ToolFrameSize);
    }

    if (this.isSmartZone()) {
      tools.push([ToolEditSmartZone]);
    } else {
      tools.push([ToolEnableSmartZone]);
    }

    return tools;
  }

  static defaultWidth() {
    return 1110;
  }

  static defaultHeight() {
    return 625;
  }

  allowedStyleCopyPasteFields(): string[] {
    return ['fill', 'opacity'];
  }

  hideText() {
    const zoom = this.canvas?.getZoom() ?? 1;
    const zoomedWidth = this.width * this.scaleX * zoom;

    if (zoomedWidth < 50) {
      this.textOpacity = 0;
    } else {
      this.textOpacity = 1;
    }
  }

  calcTextMargin() {
    return { top: 0, left: 0, right: 0, bottom: SPACER_2 };
  }

  // Add text area to frame's contained area.
  containsPoint(pointer, lines, absolute, calculate) {
    let contains = false;
    contains ||= this.containsPointInText(pointer);

    // Once it is selected, just use the default behavior.
    contains ||= super.containsPoint(pointer, lines, absolute, calculate);

    return contains;
  }

  // Check if the point is inside the frame at all.
  everContainsPoint(
    pointer: Point,
    lines?: any,
    absolute?: boolean,
    calculate?: boolean
  ) {
    return super.containsPoint(pointer, lines, absolute, calculate);
  }

  containsPointInText(pointer: fabric.Point): boolean {
    const adjustedPointer = this.canvas.restorePointerVpt(pointer);
    const zoom = this.canvas.getZoom();

    // Get text position
    const pos = this.textPosition();

    // include the smart zone icon if applicable, since it sits next to the text
    let smartZoneIconWidth = 0;
    if (this.isSmartZone()) {
      const pos = this.getSmartZoneIconPosition();
      smartZoneIconWidth = pos.scaledSpacing + pos.scaledIconWidth;
    }

    // include the auto layout icon if applicable, since it sits next to the text
    let autoLayoutIconWidth = 0;
    if (this.layoutEnabled && this.isSelected()) {
      const pos = this.getAutoLayoutIconPosition();
      autoLayoutIconWidth = pos.scaledSpacing + pos.scaledIconWidth;
    }

    const zoomScale = this.getLabelZoomScale(zoom);
    const adjustedPos = {
      left: pos.left,
      top: pos.top,
      width:
        (pos.width * zoomScale) / zoom +
        smartZoneIconWidth +
        autoLayoutIconWidth,
      height: (pos.height * zoomScale) / zoom,
    };

    return pointInRect(adjustedPointer, adjustedPos);
  }

  // We have to override this so that a frame that is completely zoomed in
  // doesn't disappear because it doesn't contain the center point.
  _containsCenterOfCanvas(pointTL, pointBR, calculate) {
    // worst case scenario the object is so big that contains the screen
    const centerPoint = {
      x: (pointTL.x + pointBR.x) / 2,
      y: (pointTL.y + pointBR.y) / 2,
    };
    if (
      super.containsPoint(centerPoint as fabric.Point, null, true, calculate)
    ) {
      return true;
    }
    return false;
  }

  _set(keyOrPath: KeyPath, value: any): fabric.Object {
    super._set(keyOrPath, value);

    if (keyOrPath === 'textContent') {
      void this.setTextContent(value);
    }

    return this;
  }

  _render(ctx: CanvasRenderingContext2D) {
    ctx.save();

    ctx.beginPath();

    // Clip out the center of the frame so the shadow doesn't show inside.
    this.renderClip(ctx);

    this.renderFrameFill(ctx);

    ctx.restore();

    this.renderTitle(ctx, [
      this.renderSmartZoneIcon.bind(this),
      this.renderAutoLayoutIcon.bind(this),
    ]);

    if (
      !this.canvas?.exporting &&
      (!this.layoutEnabled || this.shouldRenderLayoutBorder())
    ) {
      this.renderBorder(ctx);
    }
  }

  private getSmartZoneIconPosition() {
    const zoom = this.canvas.getZoom();
    const imageScale = IMAGE_SCALE;
    const zoomScale = this.getLabelZoomScale(zoom);
    const position = this.textPosition();
    const textWidth = this.getTextWidth();
    // Calculate position for the icon (to the right of the text)
    // Use the same scaling as the text to ensure consistency
    const iconWidth = 16;
    const iconHeight = 16;
    const iconSize = 1.5;

    // Calculate icon size using the same formula as text
    const scaledIconWidth =
      (iconSize * (iconWidth * zoomScale)) / (imageScale * zoom * this.scaleX);
    const scaledIconHeight =
      (iconSize * (iconHeight * zoomScale)) / (imageScale * zoom * this.scaleY);

    const spacing = 12; // spacing between text and icon
    const scaledSpacing =
      (spacing * zoomScale) / (imageScale * zoom * this.scaleX);

    const iconX =
      -this.width / 2 +
      ((position.left - this.left) * zoomScale) / this.scaleY +
      textWidth +
      scaledSpacing;

    const iconY =
      -this.height / 2 +
      (((position.top - this.top) * zoomScale) / this.scaleY -
        scaledIconHeight) /
        TITLE_ICON_HEIGHT_ADJUSTMENT;

    return { iconX, iconY, scaledIconWidth, scaledIconHeight, scaledSpacing };
  }

  private getAutoLayoutIconPosition() {
    const zoom = this.canvas.getZoom();
    const imageScale = IMAGE_SCALE;
    const zoomScale = this.getLabelZoomScale(zoom);
    const position = this.textPosition();
    const textWidth = this.getTextWidth();
    const isSmartZone = this.isSmartZone();
    // Calculate position for the icon (to the right of the text)
    // Use the same scaling as the text to ensure consistency
    const iconWidth = 19;
    const iconHeight = 19;
    const iconSize = 1.5;

    // Calculate icon size using the same formula as text
    const scaledIconWidth =
      (iconSize * (iconWidth * zoomScale)) / (imageScale * zoom * this.scaleX);
    const scaledIconHeight =
      (iconSize * (iconHeight * zoomScale)) / (imageScale * zoom * this.scaleY);

    const spacing = isSmartZone ? 16 : 12; // spacing between text and icon
    const scaledSpacing =
      (spacing * zoomScale) / (imageScale * zoom * this.scaleX);

    // If smart zone is also enabled, position after the smart zone icon
    let additionalOffset = 0;
    if (isSmartZone) {
      const smartZonePos = this.getSmartZoneIconPosition();
      additionalOffset =
        smartZonePos.scaledSpacing + smartZonePos.scaledIconWidth;
    }

    const iconX =
      -this.width / 2 +
      ((position.left - this.left) * zoomScale) / this.scaleY +
      textWidth +
      scaledSpacing +
      additionalOffset;

    const iconY =
      -this.height / 2 +
      (((position.top - this.top) * zoomScale) / this.scaleY -
        scaledIconHeight) /
        TITLE_ICON_HEIGHT_ADJUSTMENT;

    return {
      iconX,
      iconY,
      scaledIconWidth,
      scaledIconHeight,
      scaledSpacing,
    };
  }

  private renderClip(ctx: CanvasRenderingContext2D) {
    const { width, height } = this;
    const x = -width / 2;
    const y = -height / 2;

    const hasFill =
      this.layoutEnabled || (this.fill && this.fill !== 'transparent');

    if (!hasFill) {
      ctx.beginPath();
      ctx.fillStyle = 'none';
      ctx.rect(x - 100, y - 100, width + 200, height + 200);
      ctx.rect(x, y, width, height);
      ctx.clip('evenodd');
    } else {
      ctx.fillStyle = this.fill ?? 'white';
    }
  }

  private renderBorder(ctx: CanvasRenderingContext2D) {
    const width = this.getScaledWidth();
    const height = this.getScaledHeight();
    const zoom = this.canvas.getZoom();

    ctx.save();
    ctx.globalAlpha = 1;
    ctx.scale(1 / this.scaleX, 1 / this.scaleY);
    // Uses same color as grid lines
    ctx.strokeStyle = this.hovered ? '#0073CF' : '#dedede';
    ctx.lineWidth = 1 / zoom;
    ctx.strokeRect(-width / 2, -height / 2, width, height);
    ctx.restore();
  }

  private renderFrameFill(ctx: CanvasRenderingContext2D) {
    const x = -this.width / 2;
    const y = -this.height / 2;
    const isDarkMode = document.body.getAttribute('data-theme') === 'dark';

    if (this.isStylizedComponent()) {
      // Use background color of canvas as backstop to frame fill
      const backgroundFill = isDarkMode ? '#3c454d' : '#f1f1f1';

      // Render a border with curved corners
      ctx.strokeStyle = this.fill ?? 'black';
      ctx.fillStyle = backgroundFill;
      ctx.lineWidth = 4;
      this.drawRoundedRect(ctx, true, true);

      // Use a slightly faded fill for the "top" layer of the frame fill
      ctx.globalAlpha = 0.2;
      ctx.fillStyle = this.fill ?? 'white';
      this.drawRoundedRect(ctx, false, true);
    } else {
      const { width, height } = this;
      ctx.fillRect(x, y, width, height);
    }
  }

  // Render smart zone icon if this is a smart zone
  private renderSmartZoneIcon(ctx: CanvasRenderingContext2D) {
    if (this.isSmartZone() && Frame.smartZoneIcon?.complete) {
      const { iconX, iconY, scaledIconWidth, scaledIconHeight } =
        this.getSmartZoneIconPosition();

      // Draw the icon - use the same scaling formula as the text
      ctx.drawImage(
        Frame.smartZoneIcon,
        iconX,
        iconY,
        scaledIconWidth,
        scaledIconHeight
      );
    }
  }

  // Render auto layout icon if auto layout is enabled
  private renderAutoLayoutIcon(ctx: CanvasRenderingContext2D) {
    if (
      this.layoutEnabled &&
      this.isSelected() &&
      Frame.autoLayoutIcon?.complete
    ) {
      const { iconX, iconY, scaledIconWidth, scaledIconHeight } =
        this.getAutoLayoutIconPosition();

      // Draw the icon - use the same scaling formula as the text
      ctx.drawImage(
        Frame.autoLayoutIcon,
        iconX,
        iconY,
        scaledIconWidth,
        scaledIconHeight
      );
    }
  }

  private drawRoundedRect(
    ctx: CanvasRenderingContext2D,
    shouldStroke = false,
    shouldFill = false
  ) {
    const width = this.getScaledWidth();
    const height = this.getScaledHeight();
    ctx.save();
    const radius = 4;
    ctx.scale(1 / this.scaleX, 1 / this.scaleY);
    ctx.beginPath();
    ctx.moveTo(-width / 2 + radius, -height / 2);
    ctx.lineTo(width / 2 - radius, -height / 2);
    ctx.quadraticCurveTo(
      width / 2,
      -height / 2,
      width / 2,
      -height / 2 + radius
    );
    ctx.lineTo(width / 2, height / 2 - radius);
    ctx.quadraticCurveTo(width / 2, height / 2, width / 2 - radius, height / 2);
    ctx.lineTo(-width / 2 + radius, height / 2);
    ctx.quadraticCurveTo(
      -width / 2,
      height / 2,
      -width / 2,
      height / 2 - radius
    );
    ctx.lineTo(-width / 2, -height / 2 + radius);
    ctx.quadraticCurveTo(
      -width / 2,
      -height / 2,
      -width / 2 + radius,
      -height / 2
    );
    ctx.closePath();
    if (shouldStroke) {
      ctx.stroke();
    }
    if (shouldFill) {
      ctx.fill();
    }
    ctx.restore();
  }

  get selectedTextFields(): TextField[] {
    return [];
  }

  hasEmbeddedText() {
    return true;
  }

  // Layout frames don't show titles when contained inside other frames.
  showTitle() {
    if (this.layoutEnabled && this.container) {
      return false;
    }
    return true;
  }

  textClass(): string | null {
    return 'frameText';
  }

  preventTextEditing() {
    return true;
  }

  setFrameVisibility(visible: boolean) {
    this.set('notInNavigation', !visible);
  }

  getFrameVisibility() {
    return !this.notInNavigation;
  }

  async _handleKeyDown(e: KeyboardEvent): Promise<false | undefined> {
    // Edit frame title when the user presses Enter
    if (e.key === 'Enter') {
      e.preventDefault();
      this.activeTextField = this;
      this.activeTextField.startTextEditing();
      return false;
    }
  }

  sizeFrameToContents(): void {
    const { children } = this;
    if (children.length === 0) {
      return;
    }

    const maxBounds = {
      left: Number.POSITIVE_INFINITY,
      top: Number.POSITIVE_INFINITY,
      right: Number.NEGATIVE_INFINITY,
      bottom: Number.NEGATIVE_INFINITY,
    };

    // Get bounds of children
    children.forEach(child => {
      maxBounds.left = Math.min(maxBounds.left, child.left);
      maxBounds.top = Math.min(maxBounds.top, child.top);
      maxBounds.right = Math.max(
        maxBounds.right,
        child.left + child.getScaledWidth()
      );
      maxBounds.bottom = Math.max(
        maxBounds.bottom,
        child.top + child.getScaledHeight()
      );
    });

    // Size frame to child bounds with some padding
    const padding = 10;
    const diffWidth =
      this.getScaledWidth() / (maxBounds.right - maxBounds.left + padding);
    const diffHeight =
      this.getScaledHeight() / (maxBounds.bottom - maxBounds.top + padding);
    // use the difference in width to adjust the scale appropriately
    this.set('scaleX', this.scaleX / diffWidth);
    this.set('scaleY', this.scaleY / diffHeight);

    // Move frame to top left of child bounds
    this.set({
      left: maxBounds.left - padding / 2,
      top: maxBounds.top - padding / 2,
    });
    this.canvas.requestRenderAll();
  }

  isSmartZone(): boolean {
    return !!this.smartZoneConfig?.enabled;
  }

  isStylizedComponent() {
    return STYLIZED_COMPONENTS.includes(this.componentName);
  }

  private initializeComponent() {
    // Components cannot be resized
    if (this.isStylizedComponent()) {
      this.controls = {};
    }
  }

  initFrameSelectionListeners() {
    // Some of the listeners defined here should only apply to regular frames,
    // not layouts or frame subclasses. It is much simpler to apply this check
    // here than it is to try and remove the listeners in subclasses or when
    // layoutEnabled is toggled.
    const skipFrameSelectionListeners = (event?: fabric.IEvent<MouseEvent>) => {
      return (
        this.layoutEnabled ||
        (event && this.canvas._isSelectionKeyPressed(event.e))
      );
    };

    // Allow frame title to be double-clickable to edit
    this.on('mousedblclick', event => {
      if (event.pointer && this.containsPointInText(event.pointer)) {
        this.canvas.setActiveObject(this);
        this.maybeStartTextEditing(false);
      }
    });

    this.on('mousedown:before', event => {
      if (skipFrameSelectionListeners(event)) {
        return;
      }

      const activeObject = this.canvas.getActiveObject();
      if (activeObject) {
        // currently selected object is in frame
        this.childSelected = activeObject.isDescendant(this);

        // currently selected SimpleSelection is in frame
        this.childSelected ||=
          isSimpleSelection(activeObject) && this.containsObject(activeObject);
      }

      if (
        event.pointer &&
        !this.containsPointInText(event.pointer) && // Frame title are always selectable on mousedown.
        !this.isSelected() &&
        this.canvas._target === this
      ) {
        // Otherwise, frames are only selectable on mousedown if they are already
        // the active object
        this.canvas._target = null;
      }
    });

    this.on('mouseup:before', event => {
      if (skipFrameSelectionListeners(event)) {
        return;
      }
      // If the frame is zoomed in, do not allow selection
      if (
        (!this.edgesVisible || this.canvas.hasGroupSelector) &&
        this.canvas._target === this
      ) {
        this.canvas._target = null;
      }
    });

    this.on('mouseup', event => {
      if (skipFrameSelectionListeners(event)) {
        return;
      }
      // When a child of this frame was previously selected, we just deselect (
      // and do not select the frame)
      if (this.childSelected) {
        this.canvas._target = null;
      } else {
        const activeObject = this.canvas.getActiveObject();
        // This check handles the case where an object is created inside the frame.
        // In this case, it will still be the active object on mouseup here. In all
        // other cases, the active object will be cleared on mousedown
        const childCreatedInsideFrame =
          activeObject &&
          hasFrameMemberBehaviors(activeObject) &&
          activeObject.container === this.id;

        // If we left click on a selected frame, deselect it
        if (
          event.pointer &&
          this.isSelected() &&
          !this.didChange &&
          !this.containsPointInText(event.pointer) &&
          event.e.button === 0 &&
          this.canvas._target === this
        ) {
          this.canvas.discardActiveObject();
          this.canvas.requestRenderAll();
        } else if (this.canvas._target === this && !childCreatedInsideFrame) {
          // This is a workaround to fabric's default behavior for activeOn. If frames
          // were only selectable on mouseup, we could just set `activeOn: 'up'`.
          // However, since frames are sometimes selectable on mousedown, we need to
          // manually implement selection here
          this.canvas.setActiveObject(this);
          this.canvas.requestRenderAll();
        }
      }

      this.childSelected = false;
      this.didChange = false;
    });

    // Frames are not selectable on mousedown as other shapes are, so we need to
    // update the hover cursor to reflect that.
    if (!skipFrameSelectionListeners()) {
      this.hoverCursor = 'default';
    }

    const handleSelectionChange = () => {
      if (!skipFrameSelectionListeners()) {
        return;
      }

      this.hoverCursor = this.isSelected() ? 'move' : 'default';
      this.canvas.setCursor(this.hoverCursor);
    };

    this.on('selected', () => handleSelectionChange());
    this.on('deselected', () => handleSelectionChange());
  }
}

export default Frame;
